import { SentimentDistribution, TimelineItem } from '../../../SentimentAnalysis/model'

type TimelinePoint = {
  timestamp: number
  avgSentiment: number
}

export type Topic = {
  topic: string
  percentage: string
}

type TopicRange = {
  topic: string
  start: number
  end: number
}

type TopicSentiment = {
  avgSentiment: number | null
  distribution: SentimentDistribution
}

type ParticipantData = {
  timeline: TimelinePoint[]
}

type AllParticipants = {
  [participantId: string]: ParticipantData
}

export function getSentimentByTopic(allParticipants: AllParticipants, topics: Topic[], globalStart: number, globalEnd: number): Record<string, TopicSentiment> {
  const topicRanges: TopicRange[] = []
  const meetingDuration = globalEnd - globalStart
  let accum = globalStart

  for (const topic of topics) {
    const percent = parseFloat(topic.percentage.replace('%', '')) / 100
    const duration = percent * meetingDuration
    topicRanges.push({
      topic: topic.topic,
      start: accum,
      end: accum + duration,
    })
    accum += duration
  }

  const topicValues: Record<string, number[]> = {}
  const topicLabels: Record<string, string[]> = {}
  for (const t of topicRanges) {
    topicValues[t.topic] = []
    topicLabels[t.topic] = []
  }

  for (const participantId in allParticipants) {
    const timeline = allParticipants[participantId].timeline

    for (const point of timeline) {
      const score = point.avgSentiment

      const label = score >= 7 ? 'positive' : score >= 4 ? 'neutral' : 'negative'

      for (const topic of topicRanges) {
        if (point.timestamp >= topic.start && point.timestamp <= topic.end) {
          topicValues[topic.topic].push(score)
          topicLabels[topic.topic].push(label)
          break
        }
      }
    }
  }

  const output: Record<string, TopicSentiment> = {}
  for (const topic of topicRanges) {
    const scores = topicValues[topic.topic]
    const labels = topicLabels[topic.topic]
    const total = labels.length

    const distCount = { positive: 0, neutral: 0, negative: 0 }
    for (const label of labels) {
      distCount[label as keyof typeof distCount]++
    }

    const distribution: SentimentDistribution = {
      positive: total ? +Math.round((distCount.positive / total) * 100).toFixed(2) : 0.0,
      neutral: total ? +Math.round((distCount.neutral / total) * 100).toFixed(2) : 0.0,
      negative: total ? +Math.round((distCount.negative / total) * 100).toFixed(2) : 0.0,
    }

    const avg = total ? parseFloat((scores.reduce((a, b) => a + b, 0) / total).toFixed(2)) : null

    output[topic.topic] = {
      avgSentiment: avg,
      distribution,
    }
  }

  return output
}

export const filterCloseTimelineItems = (timeline: TimelineItem[], diffSec = 5): TimelineItem[] => {
  const filtered: TimelineItem[] = []
  let lastTimestamp: number | null = null

  for (const item of timeline) {
    if (lastTimestamp === null || item.timestamp - lastTimestamp >= diffSec) {
      filtered.push(item)
      lastTimestamp = item.timestamp
    }
  }

  return filtered
}
